var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Mock chart data for the Communication Network Graph widget. Nodes are built
 * from `apps/web/src/lib/demo/data/users.ts`; link counts and trends are mocked
 * but stable for a given org unit / user pairing.
 */
import { COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_NAME, COMMUNICATION_CHART_CROSS_TEAM_SPACE_CHAT_NODE_NAME, COMMUNICATION_CHART_EXTERNAL_NODE_ID, COMMUNICATION_CHART_EXTERNAL_NODE_NAME, COMMUNICATION_CHART_INTERNAL_NODE_NAME, getCommunicationChartCrossTeamSpaceNodeIdForTeam, getCommunicationChartInternalNodeIdForTeam } from '@web/components/CommunicationNetworkGraph/core/communicationChart/constants';
import { CommunicationChartNodeKindEnum } from '@web/components/CommunicationNetworkGraph/core/communicationChart/nodeKind';
import { CommunicationChartChannelEnum, CommunicationChartScopeEnum, isCommunicationChartChatChannel, isCommunicationChartEmailChannel, isCommunicationChartMeetChannel } from '@web/components/CommunicationNetworkGraph/core/communicationChart/types';
import { getOrgUnits, getUsers } from '@web/lib/demo/utils';
import { UserRole } from '@workalizer/db';
var isActiveChartUser = function (user) {
    var _a;
    return Boolean(user.isActive &&
        (user.role === UserRole.ADMIN ||
            ((_a = user.userOrgUnits) === null || _a === void 0 ? void 0 : _a.some(function (userOrgUnit) { var _a; return (_a = userOrgUnit.orgUnit) === null || _a === void 0 ? void 0 : _a.isActive; }))));
};
var getActiveChartTeamMembers = function (orgUnitId) {
    return getUsers({ orgUnitId: orgUnitId })
        .filter(isActiveChartUser)
        .map(function (user) { return ({
        id: user.id,
        name: user.name,
        email: user.email,
        avatar: user.avatar
    }); })
        .sort(function (left, right) { var _a, _b; return ((_a = left.name) !== null && _a !== void 0 ? _a : left.email).localeCompare((_b = right.name) !== null && _b !== void 0 ? _b : right.email); });
};
var getActiveChartUsers = function (orgUnitId) {
    return getActiveChartTeamMembers(orgUnitId !== null && orgUnitId !== void 0 ? orgUnitId : 0).map(function (user) {
        var _a;
        return ({
            id: user.id,
            name: (_a = user.name) !== null && _a !== void 0 ? _a : user.email,
            avatar: user.avatar
        });
    });
};
var resolveTeamScopeManagerUserId = function (orgUnitId) {
    var _a;
    var manager = getUsers({ orgUnitId: orgUnitId }).find(function (user) { return user.role === UserRole.MANAGER && isActiveChartUser(user); });
    return (_a = manager === null || manager === void 0 ? void 0 : manager.id) !== null && _a !== void 0 ? _a : null;
};
var avatarUrl = function (user) { var _a; return (_a = user.avatar) !== null && _a !== void 0 ? _a : "https://i.pravatar.cc/150?img=".concat(user.id); };
var pairSeed = function (leftId, rightId) {
    var _a = leftId < rightId ? [leftId, rightId] : [rightId, leftId], minId = _a[0], maxId = _a[1];
    return (minId + 1000) * 10000 + (maxId + 1000);
};
var trendFromSeed = function (seed) { return 0.5 + ((seed * 17) % 8) / 10; };
var directedLinkStats = function (input, trendMultiplier) { return ({
    weight: input.messages,
    sentCount: input.sent,
    receivedCount: input.received,
    previousWeight: Math.round(input.messages * trendMultiplier)
}); };
var volumeLinkStats = function (weight, trendMultiplier) { return ({
    weight: weight,
    previousWeight: Math.round(weight * trendMultiplier)
}); };
var communicationLink = function (source, target, stats) { return ({
    sourceNodeId: source,
    targetNodeId: target,
    stats: stats
}); };
var chatLikeFromSeed = function (seed, channel) {
    var base = 8 + ((seed * 13) % 45);
    var multiplier = isCommunicationChartChatChannel(channel) ? 3 : 1;
    var messages = base * multiplier;
    var sent = Math.round(messages * (0.4 + ((seed * 7) % 30) / 100));
    var received = messages - sent;
    return { messages: messages, sent: sent, received: received };
};
var buildInternalLinks = function (nodeIds, channel) {
    var links = [];
    for (var index = 0; index < nodeIds.length; index += 1) {
        for (var otherIndex = index + 1; otherIndex < nodeIds.length; otherIndex += 1) {
            var source = nodeIds[index];
            var target = nodeIds[otherIndex];
            var seed = pairSeed(source, target);
            if (isCommunicationChartEmailChannel(channel)) {
                links.push(communicationLink(source, target, directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Email), trendFromSeed(seed))));
            }
            else if (isCommunicationChartChatChannel(channel) && seed % 2 === 0) {
                links.push(communicationLink(source, target, directedLinkStats(chatLikeFromSeed(seed + 1, CommunicationChartChannelEnum.Chat), trendFromSeed(seed + 1))));
            }
            else if (isCommunicationChartMeetChannel(channel) && seed % 3 === 0) {
                links.push(communicationLink(source, target, volumeLinkStats((2 + (seed % 6)) * 45 * 60, trendFromSeed(seed + 2))));
            }
        }
    }
    return links;
};
/** Org-scope Meet: every team pair gets a visible edge (pairSeed % 3 often skips demo org unit ids). */
var buildOrgScopeTeamToTeamMeetLinks = function (orgUnitIds) {
    var links = [];
    for (var index = 0; index < orgUnitIds.length; index += 1) {
        for (var otherIndex = index + 1; otherIndex < orgUnitIds.length; otherIndex += 1) {
            var source = orgUnitIds[index];
            var target = orgUnitIds[otherIndex];
            var seed = pairSeed(source, target);
            links.push(communicationLink(source, target, volumeLinkStats((3 + (seed % 5)) * 50 * 60, trendFromSeed(seed + 2))));
        }
    }
    return links;
};
var buildSyntheticTeamLinks = function (nodeIds, channel) {
    if (nodeIds.length === 0) {
        return [];
    }
    var hubId = nodeIds[0];
    var externalSeed = pairSeed(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID);
    var crossTeamSeed = pairSeed(hubId, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID);
    var links = [];
    if (isCommunicationChartEmailChannel(channel)) {
        links.push(communicationLink(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID, directedLinkStats(chatLikeFromSeed(externalSeed, CommunicationChartChannelEnum.Email), trendFromSeed(externalSeed))));
        nodeIds.slice(1, 3).forEach(function (nodeId) {
            var seed = pairSeed(nodeId, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID);
            links.push(communicationLink(nodeId, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID, directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Email), trendFromSeed(seed))));
        });
    }
    else if (isCommunicationChartChatChannel(channel)) {
        links.push(communicationLink(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID, directedLinkStats(chatLikeFromSeed(externalSeed, CommunicationChartChannelEnum.Chat), trendFromSeed(externalSeed))), communicationLink(hubId, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID, directedLinkStats(chatLikeFromSeed(crossTeamSeed, CommunicationChartChannelEnum.Chat), trendFromSeed(crossTeamSeed))));
        nodeIds.slice(1, 4).forEach(function (nodeId) {
            var seed = pairSeed(nodeId, COMMUNICATION_CHART_EXTERNAL_NODE_ID);
            links.push(communicationLink(nodeId, COMMUNICATION_CHART_EXTERNAL_NODE_ID, directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Chat), trendFromSeed(seed))));
        });
        nodeIds.slice(1, 3).forEach(function (nodeId) {
            var seed = pairSeed(nodeId, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID);
            links.push(communicationLink(nodeId, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID, directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Chat), trendFromSeed(seed))));
        });
    }
    else {
        links.push(communicationLink(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID, volumeLinkStats((2 + (externalSeed % 4)) * 40 * 60, trendFromSeed(externalSeed + 1))), communicationLink(hubId, COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID, volumeLinkStats((3 + (crossTeamSeed % 5)) * 50 * 60, trendFromSeed(crossTeamSeed))));
    }
    return links;
};
var buildOrgScopeHubSatelliteLinks = function (orgUnitIds, channel) {
    var links = [];
    for (var index = 0; index < orgUnitIds.length; index += 1) {
        for (var otherIndex = index + 1; otherIndex < orgUnitIds.length; otherIndex += 1) {
            var source = orgUnitIds[index];
            var target = orgUnitIds[otherIndex];
            var seed = pairSeed(source, target);
            if (isCommunicationChartEmailChannel(channel)) {
                links.push(communicationLink(source, getCommunicationChartCrossTeamSpaceNodeIdForTeam(source), directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Email), trendFromSeed(seed))), communicationLink(target, getCommunicationChartCrossTeamSpaceNodeIdForTeam(target), directedLinkStats(chatLikeFromSeed(seed + 1, CommunicationChartChannelEnum.Email), trendFromSeed(seed + 1))));
            }
            else if (isCommunicationChartChatChannel(channel) && seed % 2 === 0) {
                links.push(communicationLink(source, getCommunicationChartCrossTeamSpaceNodeIdForTeam(source), directedLinkStats(chatLikeFromSeed(seed + 2, CommunicationChartChannelEnum.Chat), trendFromSeed(seed + 2))));
            }
        }
    }
    for (var _i = 0, orgUnitIds_1 = orgUnitIds; _i < orgUnitIds_1.length; _i++) {
        var orgUnitId = orgUnitIds_1[_i];
        var seed = pairSeed(orgUnitId, orgUnitId + 1);
        if (isCommunicationChartEmailChannel(channel)) {
            links.push(communicationLink(orgUnitId, getCommunicationChartInternalNodeIdForTeam(orgUnitId), directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Email), trendFromSeed(seed))));
        }
        else if (isCommunicationChartChatChannel(channel)) {
            links.push(communicationLink(orgUnitId, getCommunicationChartInternalNodeIdForTeam(orgUnitId), directedLinkStats(chatLikeFromSeed(seed + 5, CommunicationChartChannelEnum.Chat), trendFromSeed(seed + 5))));
        }
        else {
            var teamOnlyWeight = (2 + (seed % 4)) * 30 * 60;
            var crossTeamWeight = (3 + (seed % 5)) * 45 * 60;
            links.push(communicationLink(orgUnitId, getCommunicationChartInternalNodeIdForTeam(orgUnitId), volumeLinkStats(teamOnlyWeight, trendFromSeed(seed + 6))), communicationLink(orgUnitId, getCommunicationChartCrossTeamSpaceNodeIdForTeam(orgUnitId), volumeLinkStats(crossTeamWeight, trendFromSeed(seed + 7))));
        }
    }
    return links;
};
var buildSyntheticOrgLinks = function (orgUnitIds, channel) {
    if (orgUnitIds.length === 0) {
        return [];
    }
    var hubId = orgUnitIds[0];
    var seed = pairSeed(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID);
    if (isCommunicationChartEmailChannel(channel)) {
        return [
            communicationLink(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID, directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Email), trendFromSeed(seed)))
        ];
    }
    if (isCommunicationChartChatChannel(channel)) {
        return [
            communicationLink(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID, directedLinkStats(chatLikeFromSeed(seed + 1, CommunicationChartChannelEnum.Chat), trendFromSeed(seed + 1)))
        ];
    }
    return [
        communicationLink(hubId, COMMUNICATION_CHART_EXTERNAL_NODE_ID, volumeLinkStats((2 + (seed % 3)) * 40 * 60, trendFromSeed(seed + 2)))
    ];
};
var buildEmployeeScopeData = function (userId, orgUnitId, channel) {
    var _a, _b, _c, _d;
    var orgUnits = getOrgUnits().filter(function (orgUnit) { return getActiveChartUsers(orgUnit.id).length > 0; });
    var resolvedOrgUnitId = (_c = (_a = orgUnitId !== null && orgUnitId !== void 0 ? orgUnitId : getUsers({ userId: userId })
        .filter(isActiveChartUser)
        .flatMap(function (user) { var _a; return ((_a = user.userOrgUnits) !== null && _a !== void 0 ? _a : []).map(function (membership) { return membership.orgUnitId; }); })
        .find(function (id) { return id != null; })) !== null && _a !== void 0 ? _a : (_b = orgUnits[0]) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : 0;
    var teamPeople = getActiveChartUsers(resolvedOrgUnitId);
    var focusPerson = (_d = teamPeople.find(function (person) { return person.id === userId; })) !== null && _d !== void 0 ? _d : teamPeople[0];
    if (!focusPerson) {
        return {
            scope: CommunicationChartScopeEnum.Employee,
            orgUnitId: resolvedOrgUnitId,
            focusUserId: userId,
            channel: channel,
            nodes: [],
            links: []
        };
    }
    var teammatePeers = teamPeople
        .filter(function (person) { return person.id !== focusPerson.id; })
        .slice(0, 5);
    var crossTeamPeers = orgUnits
        .filter(function (orgUnit) { return orgUnit.id !== resolvedOrgUnitId; })
        .flatMap(function (orgUnit) { return getActiveChartUsers(orgUnit.id).slice(0, 2); })
        .slice(0, 6);
    var nodes = __spreadArray(__spreadArray(__spreadArray([
        {
            nodeId: focusPerson.id,
            name: focusPerson.name,
            avatarUrl: avatarUrl(focusPerson)
        }
    ], teammatePeers.map(function (person) { return ({
        nodeId: person.id,
        name: person.name,
        avatarUrl: avatarUrl(person)
    }); }), true), crossTeamPeers.map(function (person) { return ({
        nodeId: person.id,
        name: person.name,
        avatarUrl: avatarUrl(person),
        kind: CommunicationChartNodeKindEnum.CrossTeam
    }); }), true), [
        {
            nodeId: COMMUNICATION_CHART_EXTERNAL_NODE_ID,
            name: COMMUNICATION_CHART_EXTERNAL_NODE_NAME,
            kind: CommunicationChartNodeKindEnum.External
        }
    ], false);
    var links = [];
    for (var _i = 0, teammatePeers_1 = teammatePeers; _i < teammatePeers_1.length; _i++) {
        var peer = teammatePeers_1[_i];
        var seed = pairSeed(focusPerson.id, peer.id);
        if (isCommunicationChartEmailChannel(channel)) {
            links.push(communicationLink(focusPerson.id, peer.id, directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Email), trendFromSeed(seed))));
        }
        else if (isCommunicationChartChatChannel(channel)) {
            links.push(communicationLink(focusPerson.id, peer.id, directedLinkStats(chatLikeFromSeed(seed, CommunicationChartChannelEnum.Chat), trendFromSeed(seed))));
        }
        else {
            links.push(communicationLink(focusPerson.id, peer.id, volumeLinkStats((2 + (seed % 5)) * 35 * 60, trendFromSeed(seed))));
        }
    }
    for (var _e = 0, crossTeamPeers_1 = crossTeamPeers; _e < crossTeamPeers_1.length; _e++) {
        var peer = crossTeamPeers_1[_e];
        var seed = pairSeed(focusPerson.id, peer.id);
        if (isCommunicationChartEmailChannel(channel)) {
            links.push(communicationLink(focusPerson.id, peer.id, directedLinkStats(chatLikeFromSeed(seed + 3, CommunicationChartChannelEnum.Email), trendFromSeed(seed + 3))));
        }
        else if (isCommunicationChartChatChannel(channel)) {
            links.push(communicationLink(focusPerson.id, peer.id, directedLinkStats(chatLikeFromSeed(seed + 3, CommunicationChartChannelEnum.Chat), trendFromSeed(seed + 3))));
        }
        else {
            links.push(communicationLink(focusPerson.id, peer.id, volumeLinkStats((2 + (seed % 4)) * 30 * 60, trendFromSeed(seed + 3))));
        }
    }
    var externalSeed = pairSeed(focusPerson.id, COMMUNICATION_CHART_EXTERNAL_NODE_ID);
    if (isCommunicationChartEmailChannel(channel)) {
        links.push(communicationLink(focusPerson.id, COMMUNICATION_CHART_EXTERNAL_NODE_ID, directedLinkStats(chatLikeFromSeed(externalSeed, CommunicationChartChannelEnum.Email), trendFromSeed(externalSeed))));
    }
    else if (isCommunicationChartChatChannel(channel)) {
        links.push(communicationLink(focusPerson.id, COMMUNICATION_CHART_EXTERNAL_NODE_ID, directedLinkStats(chatLikeFromSeed(externalSeed, CommunicationChartChannelEnum.Chat), trendFromSeed(externalSeed))));
    }
    else {
        links.push(communicationLink(focusPerson.id, COMMUNICATION_CHART_EXTERNAL_NODE_ID, volumeLinkStats((2 + (externalSeed % 3)) * 45 * 60, trendFromSeed(externalSeed))));
    }
    return {
        scope: CommunicationChartScopeEnum.Employee,
        orgUnitId: resolvedOrgUnitId,
        focusUserId: focusPerson.id,
        channel: channel,
        nodes: nodes,
        links: links
    };
};
var buildTeamScopeData = function (orgUnitId, channel) {
    var people = getActiveChartUsers(orgUnitId);
    var personNodes = people.map(function (person) { return ({
        nodeId: person.id,
        name: person.name,
        avatarUrl: avatarUrl(person)
    }); });
    var syntheticNodes = [
        {
            nodeId: COMMUNICATION_CHART_EXTERNAL_NODE_ID,
            name: COMMUNICATION_CHART_EXTERNAL_NODE_NAME,
            kind: CommunicationChartNodeKindEnum.External
        },
        {
            nodeId: COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_ID,
            name: COMMUNICATION_CHART_CROSS_TEAM_AGGREGATE_NODE_NAME,
            kind: CommunicationChartNodeKindEnum.CrossTeamAggregate
        }
    ];
    var userIds = people.map(function (person) { return person.id; });
    var managerUserId = resolveTeamScopeManagerUserId(orgUnitId);
    return __assign(__assign({ scope: CommunicationChartScopeEnum.Team, orgUnitId: orgUnitId }, (managerUserId != null ? { managerUserId: managerUserId } : {})), { channel: channel, nodes: __spreadArray(__spreadArray([], personNodes, true), syntheticNodes, true), links: __spreadArray(__spreadArray([], buildInternalLinks(userIds, channel), true), buildSyntheticTeamLinks(userIds, channel), true) });
};
var buildOrgScopeData = function (channel) {
    var orgUnits = getOrgUnits()
        .filter(function (orgUnit) { return getActiveChartUsers(orgUnit.id).length > 0; })
        .sort(function (left, right) { return left.title.localeCompare(right.title); });
    var orgTeamNodes = orgUnits.map(function (orgUnit) { return ({
        nodeId: orgUnit.id,
        name: orgUnit.title,
        kind: CommunicationChartNodeKindEnum.Internal,
        memberCount: getActiveChartTeamMembers(orgUnit.id).length
    }); });
    var hubSatelliteNodes = orgUnits.flatMap(function (orgUnit) { return [
        {
            nodeId: getCommunicationChartInternalNodeIdForTeam(orgUnit.id),
            name: COMMUNICATION_CHART_INTERNAL_NODE_NAME,
            kind: CommunicationChartNodeKindEnum.InternalMeet
        },
        {
            nodeId: getCommunicationChartCrossTeamSpaceNodeIdForTeam(orgUnit.id),
            name: COMMUNICATION_CHART_CROSS_TEAM_SPACE_CHAT_NODE_NAME,
            kind: CommunicationChartNodeKindEnum.CrossTeamSpaceChat
        }
    ]; });
    var syntheticNodes = [
        {
            nodeId: COMMUNICATION_CHART_EXTERNAL_NODE_ID,
            name: COMMUNICATION_CHART_EXTERNAL_NODE_NAME,
            kind: CommunicationChartNodeKindEnum.External
        }
    ];
    var orgUnitIds = orgUnits.map(function (orgUnit) { return orgUnit.id; });
    return {
        scope: CommunicationChartScopeEnum.Org,
        orgUnitId: null,
        channel: channel,
        nodes: __spreadArray(__spreadArray(__spreadArray([], orgTeamNodes, true), hubSatelliteNodes, true), syntheticNodes, true),
        links: __spreadArray(__spreadArray(__spreadArray([], (isCommunicationChartMeetChannel(channel)
            ? buildOrgScopeTeamToTeamMeetLinks(orgUnitIds)
            : buildInternalLinks(orgUnitIds, channel)), true), buildSyntheticOrgLinks(orgUnitIds, channel), true), buildOrgScopeHubSatelliteLinks(orgUnitIds, channel), true)
    };
};
export var buildCommunicationNetworkGraphData = function (orgUnitId, channel, userId) {
    if (userId != null) {
        return buildEmployeeScopeData(userId, orgUnitId, channel);
    }
    return orgUnitId === null
        ? buildOrgScopeData(channel)
        : buildTeamScopeData(orgUnitId, channel);
};
export var getCommunicationNetworkGraphTeamMembers = function (orgUnitId) {
    return getActiveChartTeamMembers(orgUnitId);
};
